<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Purchase Requisitions</h5>
        <p class="page-sub">Create and track purchase requests through approval</p>
      </div>
      <div class="header-actions">
        <div class="export-wrap" ref="exportWrap">
          <button class="btn-export" @click="exportMenuOpen = !exportMenuOpen" :disabled="exporting">
            <font-awesome-icon icon="download" /> Export <font-awesome-icon icon="chevron-down" style="font-size:10px" />
          </button>
          <div v-if="exportMenuOpen" class="export-menu">
            <button class="export-opt" @click="openExportModal('csv')"><font-awesome-icon icon="file-csv" class="me-2" />Export CSV</button>
            <button class="export-opt" @click="openExportModal('pdf')"><font-awesome-icon icon="file-pdf" class="me-2" />Export PDF</button>
          </div>
        </div>
        <button class="btn-primary" @click="$router.push('/procurement/purchase-requisitions/create')">
          <font-awesome-icon icon="plus" class="me-1" /> New Requisition
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filter-bar">
      <input v-model="search" @input="debouncedFetch" class="filter-input" placeholder="Search by PR number, title, project..." />
      <select v-model="filterStatus" @change="fetchPRs" class="filter-select">
        <option value="">All Statuses</option>
        <option value="draft">Draft</option>
        <option value="submitted">Submitted</option>
        <option value="under_review">Under Review</option>
        <option value="returned">Returned</option>
        <option value="approved">Approved</option>
        <option value="sourcing">Sourcing</option>
        <option value="partially_sourced">Partially Sourced</option>
        <option value="fully_sourced">Fully Sourced</option>
        <option value="partially_received">Partially Received</option>
        <option value="received">Received</option>
        <option value="closed">Closed</option>
        <option value="cancelled">Cancelled</option>
      </select>
      <select v-model="filterUrgency" @change="fetchPRs" class="filter-select">
        <option value="">All Urgency</option>
        <option value="normal">Normal</option>
        <option value="urgent">Urgent</option>
        <option value="emergency">Emergency</option>
      </select>
      <input type="date" v-model="dateFrom" class="filter-input" style="max-width:160px" />
      <input type="date" v-model="dateTo" class="filter-input" style="max-width:160px" />
      <button class="btn-apply" @click="applyDateFilter">Apply</button>
      <button class="btn-clear" @click="clearDateFilter" v-if="dateFrom || dateTo">Clear</button>
      <div class="per-page-wrap">
        <label class="per-page-label">Show</label>
        <select class="per-page-select" v-model.number="perPage" @change="page = 1; fetchPRs()">
          <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
        </select>
        <span class="per-page-label">per page</span>
      </div>
    </div>

    <!-- Table -->
    <div class="card">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div><p>Loading...</p>
      </div>

      <div v-else-if="!prs.length" class="empty-state">
        <font-awesome-icon icon="file-alt" class="empty-icon" />
        <p>No purchase requisitions found</p>
        <span>Create your first requisition to get started</span>
      </div>

      <table v-else class="data-table">
        <thead>
          <tr>
            <th>PR Number</th>
            <th>Title</th>
            <th>Project / Cost Code</th>
            <th>Need By</th>
            <th>Estimated Total</th>
            <th>Urgency</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="pr in prs" :key="pr.id" class="row-link" @click="goToDetail(pr.id)">
            <td><span class="mono">{{ pr.pr_number }}</span></td>
            <td>
              <div class="pr-title">{{ pr.title }}</div>
              <div class="pr-sub" v-if="pr.category">{{ pr.category.name }}</div>
            </td>
            <td>
              <div v-if="pr.project">{{ pr.project }}</div>
              <div class="pr-sub" v-if="pr.cost_code">{{ pr.cost_code }}</div>
              <span v-if="!pr.project && !pr.cost_code" class="text-muted">—</span>
            </td>
            <td>
              <span v-if="pr.need_by_date" :class="isOverdue(pr.need_by_date) ? 'date-overdue' : ''">
                {{ formatDate(pr.need_by_date) }}
              </span>
              <span v-else class="text-muted">—</span>
            </td>
            <td>
              <span v-if="pr.estimated_total">{{ formatCurrency(pr.estimated_total, pr.currency) }}</span>
              <span v-else class="text-muted">—</span>
            </td>
            <td><span :class="['urgency-badge', `urgency-badge--${pr.urgency}`]">{{ capitalise(pr.urgency) }}</span></td>
            <td><span :class="['status-badge', `status-badge--${pr.status}`]">{{ formatStatus(pr.status) }}</span></td>
            <td @click.stop>
              <div class="action-menu">
                <button class="btn-icon" @click="goToDetail(pr.id)" title="View"><font-awesome-icon icon="eye" /></button>
                <button v-if="['draft','returned'].includes(pr.status)" class="btn-icon" @click="$router.push(`/procurement/purchase-requisitions/${pr.id}/edit`)" title="Edit"><font-awesome-icon icon="edit" /></button>
                <button v-if="['draft','returned'].includes(pr.status)" class="btn-icon" @click="submitPR(pr)" title="Submit"><font-awesome-icon icon="paper-plane" /></button>
                <button v-if="['draft','cancelled'].includes(pr.status)" class="btn-icon btn-icon--danger" @click="confirmDelete(pr)" title="Delete"><font-awesome-icon icon="trash" /></button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="pagination-bar" v-if="meta && meta.last_page > 1">
        <span class="pagination-info">Showing {{ meta.from }}–{{ meta.to }} of {{ meta.total }}</span>
        <div class="pagination-controls">
          <button class="pg-btn" :disabled="meta.current_page <= 1" @click="changePage(1)">«</button>
          <button class="pg-btn" :disabled="meta.current_page <= 1" @click="changePage(meta.current_page - 1)">‹ Prev</button>
          <span class="pg-pages">
            <button v-for="p in pageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === meta.current_page }" @click="changePage(p)">{{ p }}</button>
          </span>
          <button class="pg-btn" :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.current_page + 1)">Next ›</button>
          <button class="pg-btn" :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.last_page)">»</button>
        </div>
      </div>
    </div>

    <!-- ── Export Column Picker ── -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-ghost-cp" @click="showExportModal = false">Cancel</button>
            <button class="btn-primary-cp" :disabled="selectedExportCols.length === 0 || exporting" @click="doExport()">
              <font-awesome-icon :icon="exporting ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exporting" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Delete Confirm -->
    <div v-if="deleteTarget" class="modal-backdrop" @click.self="deleteTarget = null">
      <div class="modal-box">
        <div class="modal-header">
          <h6>Delete Requisition</h6>
          <button class="btn-close-modal" @click="deleteTarget = null">&times;</button>
        </div>
        <div class="modal-body">
          <p>Delete <strong>{{ deleteTarget.pr_number }}</strong> — <em>{{ deleteTarget.title }}</em>? This cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="deleteTarget = null">Cancel</button>
          <button class="btn-danger" :disabled="deleting" @click="deletePR">{{ deleting ? 'Deleting...' : 'Delete' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default {
  name: 'PurchaseRequisitions',
  setup() { return { toast: useToast() }; },
  data() {
    return {
      prs: [], meta: null, loading: false,
      search: '', filterStatus: '', filterUrgency: '', page: 1, perPage: 5,
      dateFrom: '', dateTo: '', exportMenuOpen: false, exporting: false,
      deleteTarget: null, deleting: false,
      searchTimer: null,
      showExportModal: false, pendingExportFmt: 'csv', selectedExportCols: [],
      exportColumnDefs: [
        { key: 'pr_number',  label: 'PR Number',    pdfWidth: 90  },
        { key: 'title',      label: 'Title',         pdfWidth: 140 },
        { key: 'project',    label: 'Project',       pdfWidth: 90  },
        { key: 'cost_code',  label: 'Cost Code',     pdfWidth: 75  },
        { key: 'need_by',    label: 'Need By',       pdfWidth: 85  },
        { key: 'est_total',  label: 'Est. Total',    pdfWidth: 90  },
        { key: 'urgency',    label: 'Urgency',       pdfWidth: 75  },
        { key: 'status',     label: 'Status',        pdfWidth: 116 },
      ],
    };
  },
  computed: {
    pageRange() {
      if (!this.meta || !this.meta.last_page) return [];
      const cur = this.meta.current_page, last = this.meta.last_page;
      const range = [];
      for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i);
      return range;
    },
  },
  mounted() {
    this.fetchPRs();
    document.addEventListener('click', this.onOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onOutsideClick);
  },
  methods: {
    async fetchPRs() {
      this.loading = true;
      try {
        const { data } = await axios.get('/procurement/purchase-requisitions', {
          params: {
            search: this.search, status: this.filterStatus, urgency: this.filterUrgency, page: this.page, per_page: this.perPage,
            from: this.dateFrom || undefined, to: this.dateTo || undefined,
          },
        });
        this.prs  = data.data.data;
        this.meta = data.data;
      } catch { this.toast.error('Failed to load purchase requisitions'); }
      finally { this.loading = false; }
    },
    debouncedFetch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.page = 1; this.fetchPRs(); }, 350);
    },
    changePage(p) { this.page = p; this.fetchPRs(); },
    applyDateFilter() { this.page = 1; this.fetchPRs(); },
    clearDateFilter() { this.dateFrom = ''; this.dateTo = ''; this.page = 1; this.fetchPRs(); },
    openExportModal(format) {
      this.pendingExportFmt   = format;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
      this.exportMenuOpen     = false;
    },

    getPRValue(pr, key) {
      switch (key) {
        case 'pr_number': return pr.pr_number || '—';
        case 'title':     return pr.title || '—';
        case 'project':   return pr.project || '—';
        case 'cost_code': return pr.cost_code || '—';
        case 'need_by':   return pr.need_by_date ? this.formatDate(pr.need_by_date) : '—';
        case 'est_total': return pr.estimated_total ? this.formatCurrency(pr.estimated_total, pr.currency) : '—';
        case 'urgency':   return this.capitalise(pr.urgency);
        case 'status':    return this.formatStatus(pr.status);
        default:          return '—';
      }
    },

    async doExport() {
      if (!this.selectedExportCols.length) return;
      this.exporting       = true;
      this.showExportModal = false;
      try {
        const { data } = await axios.get('/procurement/purchase-requisitions', {
          params: { page: 1, per_page: 10000,
                    search: this.search || undefined,
                    status: this.filterStatus || undefined,
                    urgency: this.filterUrgency || undefined,
                    from: this.dateFrom || undefined,
                    to: this.dateTo || undefined },
        });
        const rows = data.data?.data ?? [];
        const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        const headers = cols.map(c => c.label);
        const body    = rows.map(r => cols.map(c => this.getPRValue(r, c.key)));
        const date    = new Date().toISOString().slice(0, 10);
        if (this.pendingExportFmt === 'csv') {
          const csv  = [headers, ...body].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `purchase-requisitions-${date}.csv`; a.click();
          URL.revokeObjectURL(url);
        } else {
          const doc      = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable   = 761;
          const totalW   = cols.reduce((s, c) => s + c.pdfWidth, 0);
          const scale    = usable / totalW;
          const colStyles = {};
          cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
          doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(26, 43, 69);
          doc.text('Purchase Requisitions', 40, 46);
          doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(122, 144, 176);
          doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62);
          doc.autoTable({
            startY: 78, head: [headers], body,
            styles: { fontSize: 7.5, cellPadding: 3.5, textColor: [26, 43, 69], overflow: 'ellipsize' },
            headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: colStyles, tableWidth: usable, margin: { left: 40, right: 40 },
            didDrawPage(d) {
              doc.setFontSize(8); doc.setTextColor(192, 202, 214);
              doc.text(`Page ${d.pageNumber}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 16, { align: 'center' });
            },
          });
          doc.save(`purchase-requisitions-${date}.pdf`);
        }
      } catch { this.toast?.error('Export failed. Please try again.'); }
      finally  { this.exporting = false; }
    },
    onOutsideClick(e) {
      if (this.$refs.exportWrap && !this.$refs.exportWrap.contains(e.target)) this.exportMenuOpen = false;
    },
    goToDetail(id) { this.$router.push(`/procurement/purchase-requisitions/${id}`); },
    async submitPR(pr) {
      if (!confirm(`Submit ${pr.pr_number} for approval?`)) return;
      try {
        await axios.post(`/procurement/purchase-requisitions/${pr.id}/submit`);
        this.toast.success('Requisition submitted');
        this.fetchPRs();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to submit');
      }
    },
    confirmDelete(pr) { this.deleteTarget = pr; },
    async deletePR() {
      this.deleting = true;
      try {
        await axios.delete(`/procurement/purchase-requisitions/${this.deleteTarget.id}`);
        this.toast.success('Requisition deleted');
        this.deleteTarget = null;
        this.fetchPRs();
      } catch { this.toast.error('Failed to delete'); }
      finally { this.deleting = false; }
    },
    formatStatus(s) {
      const map = { draft:'Draft', submitted:'Submitted', under_review:'Under Review', returned:'Returned',
        approved:'Approved', sourcing:'Sourcing', partially_sourced:'Partially Sourced',
        fully_sourced:'Fully Sourced', partially_received:'Partially Received', received:'Received',
        closed:'Closed', cancelled:'Cancelled' };
      return map[s] ?? s;
    },
    capitalise(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; },
    formatDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
    },
    formatCurrency(amount, currency) {
      const sym = { NGN:'₦', GBP:'£', USD:'$', EUR:'€' }[currency] ?? (currency ?? '');
      return sym + Number(amount).toLocaleString('en', { minimumFractionDigits: 2 });
    },
    isOverdue(d) { return d && new Date(d) < new Date(); },
  },
};
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.me-1        { margin-right:4px; }

.filter-bar   { display:flex; gap:12px; margin-bottom:16px; flex-wrap:wrap; }
.filter-input { flex:1; min-width:200px; padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; outline:none; }
.filter-input:focus { border-color:#004AAD; }
.filter-select { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; background:#fff; cursor:pointer; outline:none; }

.card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.loading-state,.empty-state { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; color:#7a90b0; gap:10px; }
.empty-icon { font-size:40px; color:#dde5f0; }
.empty-state p { font-size:15px; font-weight:600; color:#1a2b45; margin:0; }

.data-table { width:100%; border-collapse:collapse; }
.data-table th { padding:11px 16px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; text-align:left; white-space:nowrap; }
.data-table td { padding:13px 16px; font-size:13px; color:#1a2b45; border-bottom:1px solid #f0f4f8; vertical-align:middle; }
.row-link { cursor:pointer; transition:background .12s; }
.row-link:hover { background:#f8fafc; }
.row-link:last-child td { border-bottom:none; }

.pr-title    { font-weight:600; }
.pr-sub      { font-size:11.5px; color:#7a90b0; margin-top:2px; }
.mono        { font-family:monospace; font-size:12px; color:#004AAD; font-weight:600; }
.text-muted  { color:#aab8cc; font-size:12px; }
.date-overdue { color:#dc2626; font-weight:600; }

.urgency-badge { display:inline-block; font-size:11.5px; font-weight:600; border-radius:20px; padding:3px 10px; }
.urgency-badge--normal    { background:#f1f5f9; color:#64748b; }
.urgency-badge--urgent    { background:#fff7ed; color:#c2410c; }
.urgency-badge--emergency { background:#fee2e2; color:#dc2626; }

.status-badge { display:inline-block; font-size:11.5px; font-weight:600; border-radius:20px; padding:3px 10px; white-space:nowrap; }
.status-badge--draft            { background:#f1f5f9; color:#64748b; }
.status-badge--submitted        { background:#eff6ff; color:#1d4ed8; }
.status-badge--under_review     { background:#fff7ed; color:#c2410c; }
.status-badge--returned         { background:#fef9c3; color:#854d0e; }
.status-badge--approved         { background:#dcfce7; color:#15803d; }
.status-badge--sourcing         { background:#f0f5ff; color:#004AAD; }
.status-badge--partially_sourced { background:#e0f2fe; color:#0369a1; }
.status-badge--fully_sourced      { background:#d1fae5; color:#065f46; }
.status-badge--partially_received { background:#fef3c7; color:#92400e; }
.status-badge--received           { background:#dcfce7; color:#166534; }
.status-badge--closed             { background:#f1f5f9; color:#475569; }
.status-badge--cancelled        { background:#fee2e2; color:#dc2626; }

.action-menu { display:flex; gap:6px; }
.btn-icon { width:30px; height:30px; border-radius:7px; border:1px solid #e4eaf2; background:#fff; color:#1a2b45; display:flex; align-items:center; justify-content:center; font-size:13px; cursor:pointer; transition:all .15s; }
.btn-icon:hover { background:#f4f7fb; }
.btn-icon--danger:hover { background:#fee2e2; border-color:#fecaca; color:#dc2626; }

.pagination-bar      { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-top:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
.pagination-info     { font-size:13px; color:#7a90b0; }
.pagination-controls { display:flex; align-items:center; gap:4px; }
.pg-btn  { padding:6px 11px; border:1px solid #dde5f0; border-radius:7px; background:#fff; color:#1a2b45; font-size:13px; cursor:pointer; transition:all .12s; }
.pg-btn:hover:not(:disabled) { background:#f4f7fb; border-color:#c8d6e8; }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-pages { display:flex; gap:3px; }
.pg-num  { min-width:34px; padding:6px 8px; border:1px solid #dde5f0; border-radius:7px; background:#fff; color:#1a2b45; font-size:13px; cursor:pointer; transition:all .12s; text-align:center; }
.pg-num:hover { background:#f4f7fb; border-color:#c8d6e8; }
.pg-num--active { background:#004AAD; border-color:#004AAD; color:#fff; font-weight:600; }
.per-page-wrap  { display:flex; align-items:center; gap:6px; }
.per-page-label { font-size:13px; color:#5a6a85; white-space:nowrap; }
.per-page-select { padding:7px 10px; border:1.5px solid #dde5f0; border-radius:7px; font-size:13px; color:#1a2b45; background:#fff; cursor:pointer; outline:none; }

.modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:20px; }
.modal-box  { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 24px; border-bottom:1px solid #e4eaf2; }
.modal-header h6 { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.btn-close-modal { background:transparent; border:none; font-size:22px; color:#7a90b0; cursor:pointer; }
.modal-body { padding:20px 24px; font-size:13.5px; color:#1a2b45; }
.modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:16px 24px; border-top:1px solid #e4eaf2; }

.btn-primary { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary:hover { background:#003b8e; }
.btn-outline { display:inline-flex; align-items:center; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-outline:hover { background:#f4f7fb; }
.btn-danger  { display:inline-flex; align-items:center; background:#dc2626; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; }
.btn-danger:hover:not(:disabled) { background:#b91c1c; }
.btn-danger:disabled { opacity:.5; cursor:not-allowed; }

.spinner { width:32px; height:32px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }

@media(max-width:768px) {
  .data-table th:nth-child(3), .data-table td:nth-child(3),
  .data-table th:nth-child(5), .data-table td:nth-child(5) { display:none; }
}
.header-actions{display:flex;align-items:center;gap:10px}
.export-wrap{position:relative}
.btn-export{background:#f4f7fb;color:#3d5068;border:1.5px solid #e4eaf2;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:5px;white-space:nowrap}
.btn-export:hover{background:#e8edf5}
.btn-export:disabled{opacity:.55;cursor:not-allowed}
.export-menu{position:absolute;top:calc(100% + 6px);right:0;background:#fff;border:1.5px solid #e4eaf2;border-radius:10px;box-shadow:0 6px 20px rgba(0,0,0,.1);z-index:200;min-width:160px;overflow:hidden}
.export-opt{display:flex;align-items:center;width:100%;padding:10px 14px;background:none;border:none;font-size:13px;color:#1a2b45;cursor:pointer;text-align:left}
.export-opt:hover{background:#f4f7fb}
.btn-apply{background:#004AAD;color:#fff;border:none;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-apply:hover{background:#003b8e}
.btn-clear{background:#f4f7fb;color:#3d5068;border:1.5px solid #e4eaf2;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-clear:hover{background:#e8edf5}
.col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
.col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
.col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
.col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
.col-picker-close:hover { color:#1a2b45; }
.col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
.col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
.col-picker-link:hover { text-decoration:underline; }
.col-picker-sep      { color:#c5d3e0; }
.col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
.col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
.col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
.col-picker-item:hover { background:#f4f7fb; }
.col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
.col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
.col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
.btn-ghost-cp   { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:1px solid #e4eaf2; cursor:pointer; font-family:inherit; }
.btn-ghost-cp:hover { background:#e4eaf2; }
.btn-primary-cp { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-cp:hover:not(:disabled) { background:#003b8e; }
.btn-primary-cp:disabled { opacity:.45; cursor:not-allowed; }
.fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity:0; }
</style>
